import type {ReactElement} from 'react';
import {useEffect, useMemo, useState} from 'react';
import MarkdownRenderer from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer';
import {AiThreeDotsLoader} from '@Components/ai-chat-window/components/ai-three-dots-loader/ai-three-dots-loader';
import type {CyclingLoadingTextProps} from './cycling-loading-text.types';
import styles from './cycling-loading-text.module.scss';

const TIME_TO_NEXT_LOADING_TEXT_MS = 4000;

const shuffle = <T,>(items: T[]): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export function CyclingLoadingText({loadingTexts}: CyclingLoadingTextProps): ReactElement {
  const shuffledTexts = useMemo(() => (loadingTexts.length > 1 ? shuffle(loadingTexts) : loadingTexts), [loadingTexts]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex((prev) => (prev < shuffledTexts.length ? prev : 0));
  }, [shuffledTexts.length]);

  useEffect(() => {
    if (shuffledTexts.length <= 1) {
      return;
    }

    const timer = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % shuffledTexts.length);
    }, TIME_TO_NEXT_LOADING_TEXT_MS);

    return (): void => {
      clearTimeout(timer);
    };
  }, [currentIndex, shuffledTexts.length]);

  return (
    <div className={`${styles.container} flex-v-center`}>
      <MarkdownRenderer content={shuffledTexts[currentIndex]} suffix={<AiThreeDotsLoader className={'spacing-m-l-1'} />} />
    </div>
  );
}
